<template>
  <div>
    <div class="statement-data">
      <!--  DESKTOP view starts     -->
      <b-row class="align-items-center d-none d-md-flex">
        <b-col :md="11" class="text-left">
          <b-row class="align-items-center">
            <b-col :md="2" class="text-left">
              <b-link
                :to="{
                  name: 'invoicesSingle',
                  params: { id: this.statement.invoice_id }
                }"
                target="_blank"
                >{{ invoiceNumberOrId.ref }}</b-link
              >
              <span>{{ invoiceNumberOrId.id }}</span>
            </b-col>
            <b-col :md="2" class="text-left">{{
              statement.dated | moment('MMM D, YYYY')
            }}</b-col>
            <b-col :md="2" class="text-left">
              <span class="text-capitalize">{{ statement.type }}</span>
            </b-col>
            <b-col :md="2" class="font-weight-bold">
              <template v-if="statement.credit">
                {{ statement.credit | currency(currency) }}
                <div class="conversion-data">
                  {{ renderConversionData }}
                </div>
              </template>
            </b-col>
            <b-col :md="2" class="font-weight-bold">
              <template v-if="statement.debit"
                >{{ statement.debit | currency(currency) }}
                <div class="conversion-data">
                  {{ renderConversionData }}
                </div>
              </template>
            </b-col>
            <b-col :md="2" class="text-right" v-if="showOutstandingBalance">
              <span v-if="bracket">({{ balance | currency(currency) }})</span>
              <span v-else>{{ balance | currency(currency) }}</span>
            </b-col>
            <b-col :md="2" class="text-right" v-else>
              <span>N.A</span>
            </b-col>
          </b-row>
        </b-col>
        <b-col :cols="1" class="text-right">
          <b-row class="align-items-center">
            <b-col :cols="12" class="text-center">
              <b-link
                v-b-toggle="`collapse-${statement.type}-${statement.id}`"
                v-if="
                  Object.keys(statement.transaction_data).length > 0 ||
                  (statement.set_offs && statement.set_offs.length > 0) ||
                  statement.type === 'charges'
                "
                class="view-data-link"
                :class="{ active: opened }"
                v-track-click
                @click="opened = !opened"
                :id="`collapse-${statement.type}-${statement.id}`"
              >
                <span
                  v-b-tooltip.hover
                  :title="$t('invoices.single.statement.viewDetails')"
                  v-if="!opened"
                >
                  <b-icon-chevron-down />
                </span>
                <span
                  v-b-tooltip.hover
                  :title="$t('invoices.single.statement.closeDetails')"
                  v-else
                >
                  <b-icon-chevron-up />
                </span>
              </b-link>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <!--  DESKTOP view ends     -->
      <!--  MOBILE view starts     -->
      <b-row class="justify-content-between d-flex d-md-none position-relative">
        <b-col :cols="'auto'" class="text-left">
          <div>
            <b>{{ statement.dated | moment('MMM D, YYYY') }}</b>
          </div>
          <div>
            <span class="text-capitalize">{{ statement.type }} / </span>
            <b-link
              :to="{
                name: 'invoicesSingle',
                params: { id: this.statement.invoice_id }
              }"
              target="_blank"
              >{{ invoiceNumberOrId.ref }}</b-link
            >
            <span>{{ invoiceNumberOrId.id }}</span>
          </div>
        </b-col>
        <b-col :cols="'auto'" class="text-right">
          <template v-if="statement.credit"
            ><b
              >{{ statement.credit | currency(currency) }}
              <span class="text-primary">(Cr)</span></b
            ></template
          >
          <template v-if="statement.debit"
            ><b
              >{{ statement.debit | currency(currency) }}
              <span class="text-danger">(Dr)</span></b
            ></template
          >
          <div>
            <div class="text-right" v-if="showOutstandingBalance">
              <span v-if="bracket"
                >({{ balance | currency(currency) }}) <i>OB</i></span
              >
              <span v-else>{{ balance | currency(currency) }} <i>OB</i></span>
            </div>
            <div class="text-right" v-else>
              <span>N.A</span>
            </div>
          </div>
        </b-col>
      </b-row>
      <b-row class="d-flex d-md-none">
        <b-col :cols="'auto'" class="text-right position-relative mobile-stuff">
          <b-link
            v-b-toggle="`collapse-${statement.type}-${statement.id}`"
            v-if="
              Object.keys(statement.transaction_data).length > 0 ||
              (statement.set_offs && statement.set_offs.length > 0) ||
              statement.type === 'charges'
            "
            class="view-data-link"
            :class="{ active: opened }"
            v-track-click
            @click="opened = !opened"
            :id="`collapse-${statement.type}-${statement.id}`"
          >
            <span
              v-b-tooltip.hover
              :title="$t('invoices.single.statement.viewDetails')"
              v-if="!opened"
              ><b-icon-chevron-down
            /></span>
            <span
              v-b-tooltip.hover
              :title="$t('invoices.single.statement.closeDetails')"
              v-else
              ><b-icon-chevron-up
            /></span>
          </b-link>
        </b-col>
      </b-row>
      <!--  MOBILE view ends     -->
    </div>
    <!-- Transaction Data Component -->
    <transaction-data
      v-if="Object.keys(statement.transaction_data).length > 0"
      :statement="statement"
      :invoiceId="invoiceId"
      :currency="currency"
      :transactionData="statement.transaction_data"
      :opened="showContent"
      :linkedInvoiceCols="statement.type == 'payment' ? 7 : 9"
    />
    <!-- SetOff Data Component -->
    <set-off-data
      v-if="statement.set_offs && statement.set_offs.length > 0"
      :statement="statement"
      :setOffData="statement.set_offs"
      :opened="showContent"
      :setoffText="`invoices.single.statement.${
        statement.type == 'payment' ? 'setOffFrom' : 'setOffAgainst'
      }`"
      :linkedInvoiceCols="statement.type == 'payment' ? 7 : 9"
    />
    <!-- Charges Component -->
    <charges-data
      v-if="statement.type === 'charges'"
      :fees="fees"
      :statement="statement"
      :currency="currency"
      :opened="showContent"
    />
  </div>
</template>
<script>
import TransactionData from '@/components/invoices/Details/v2/TransactionData/'
import SetOffData from '@/components/invoices/Details/v2/SetOffData/'
import ChargesData from '@/components/invoices/Details/v2/ChargesData/'
import { commaSeparator } from '@/utils/common'

export default {
  data() {
    return {
      opened: this.showContent
    }
  },
  computed: {
    balance() {
      let bal
      bal =
        this.component === 'passbook'
          ? this.outstandingValue
          : parseFloat(this.statement.balance)
      return Math.sign(bal) === -1 ? bal * -1 : bal
    },
    bracket() {
      return this.component === 'passbook'
        ? Math.sign(this.outstandingValue) < 0
        : Math.sign(parseFloat(this.statement.balance)) < 0
    },
    invoiceNumberOrId() {
      return this.component === 'passbook'
        ? { ref: this.statement.invoice_reference }
        : { id: this.statement.id }
    },
    renderConversionData() {
      const switchTransaction =
        this.statement.transaction_data.switch_transaction || {}
      const transactionAmount = switchTransaction.transaction_amount
      const rate = switchTransaction.rate
      const baseCurrency = switchTransaction.base_currency
      return transactionAmount
        ? `₹ ${commaSeparator(
            transactionAmount
          )} at ₹${rate} per ${baseCurrency}`
        : null
    }
  },
  props: [
    'statement',
    'currency',
    'fees',
    'invoiceId',
    'component',
    'outstandingValue',
    'showOutstandingBalance',
    'showContent'
  ],
  components: {
    TransactionData,
    SetOffData,
    ChargesData
  }
}
</script>
<style lang="scss" scoped>
.mobile-stuff {
  width: 100%;
  padding: 0 5px;
  top: 10px;

  .view-data-link {
    width: 100%;
    border-radius: 0;
  }
}
.statement {
  .conversion-data {
    font-size: 0.75rem;
    font-weight: normal;
  }
}
</style>
